import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Calendar,
  ArrowLeft,
  ArrowRight,
  Share2,
  Clock,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Check,
  Copy,
  List,
  ShieldCheck,
  ShoppingCart,
  CreditCard,
  BarChart3,
  Brain,
  Sparkles,
  ChevronDown,
  BookOpen
} from 'lucide-react';
import { format } from 'date-fns';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { blogService, BlogPost } from '../services/blogService';
import { newsletterService } from '../services/newsletterService';
import { useTheme } from '../context/ThemeContext';
import { useSeo } from '../hooks/useSeo';

const AI_FRAUD_SLUG = 'how-to-use-ai-to-detect-fraudulent-transactions-in-e-commerce';

const AI_FRAUD_INITIAL_POST: BlogPost = {
  id: 44,
  title: 'How to Use AI to Detect Fraudulent Transactions in E-Commerce',
  slug: 'how-to-use-ai-to-detect-fraudulent-transactions-in-e-commerce',
  excerpt: 'Learn how e-commerce businesses use Artificial Intelligence and machine learning to detect fraudulent transactions in real time. Discover key fraud types, AI risk-scoring architectures, and a practical step-by-step implementation guide to block bad actors without frustrating legitimate shoppers.',
  featured_image: 'uploads/blogs/1785990183_6a740c2742e1e.jpg',
  image_url: 'https://www.princeitsolution.com/uploads/blogs/1785990183_6a740c2742e1e.jpg',
  author: 'Tiya Chavda',
  status: 'published',
  published_at: '2026-08-06T09:53:04.000000Z',
  created_at: '2026-08-06T09:53:04.000000Z',
  updated_at: '2026-08-07T10:46:45.000000Z'
};

interface TocItem {
  id: string;
  num: string;
  title: string;
}

const AI_FRAUD_TOC: TocItem[] = [
  { id: 'why-traditional-rule-based-systems-fail', num: '01', title: 'Why Rule-Based Systems Fail' },
  { id: 'types-of-e-commerce-fraud-ai-can-spot-instantly', num: '02', title: 'Types of Fraud AI Spots Instantly' },
  { id: 'how-ai-fraud-detection-works-behind-the-scenes', num: '03', title: 'How AI Fraud Detection Works' },
  { id: 'static-rules-vs-ai-fraud-detection-side-by-side', num: '04', title: 'Static Rules vs. AI: Side-by-Side' },
  { id: '5-steps-to-implement-ai-fraud-detection-in-your-e-commerce-store', num: '05', title: '5 Steps to Implement AI Detection' },
  { id: 'best-practices-to-keep-conversion-rates-high', num: '06', title: 'Best Practices for High Conversions' },
  { id: 'the-bottom-line', num: '07', title: 'The Bottom Line' }
];

interface CategoryInfo {
  id: string;
  name: string;
  badge: string;
  type: string;
  tags: string[];
}

const getArticleCategoryInfo = (blog: BlogPost | null): CategoryInfo => {
  if (!blog) {
    return {
      id: 'general',
      name: 'Technology',
      badge: 'Technology Insights',
      type: 'Technical Guide',
      tags: ['#Technology', '#SoftwareEngineering', '#Innovation']
    };
  }

  const title = (blog.title || '').toLowerCase();
  const text = `${title} ${blog.excerpt || ''}`.toLowerCase();

  // AI & Cyber Security
  if (
    title.includes('fraud') ||
    title.includes('cybersecurity') ||
    title.includes('zero trust') ||
    title.includes('zero-trust') ||
    title.includes('privacy-safe') ||
    text.includes('cyber protection')
  ) {
    return {
      id: 'ai-security',
      name: 'AI & Cyber Security',
      badge: 'E-Commerce Security & AI',
      type: 'Technical Guide',
      tags: ['#ArtificialIntelligence', '#EcommerceFraud', '#MachineLearning', '#CyberSecurity']
    };
  }

  // Artificial Intelligence & Machine Learning
  if (
    title.includes('ai ') ||
    title.includes('ai:') ||
    title.includes('ai vs') ||
    title.includes('artificial intelligence') ||
    title.includes('machine learning') ||
    title.includes('chatbot') ||
    title.includes('generative ai') ||
    title.includes('adaptive learning') ||
    title.includes('code assistants') ||
    text.includes('conversational ai')
  ) {
    return {
      id: 'ai-ml',
      name: 'AI & Future Tech',
      badge: 'Artificial Intelligence & AI',
      type: 'Engineering Insights',
      tags: ['#ArtificialIntelligence', '#MachineLearning', '#FutureTech', '#Automation']
    };
  }

  // E-Commerce
  if (
    title.includes('ecommerce') ||
    title.includes('e-commerce') ||
    title.includes('shopify') ||
    title.includes('multi-channel') ||
    title.includes('online store')
  ) {
    return {
      id: 'ecommerce',
      name: 'E-Commerce Engineering',
      badge: 'E-Commerce & Digital Retail',
      type: 'Strategic Playbook',
      tags: ['#EcommerceWebsite', '#WebDevelopment', '#OnlineRetail', '#ConversionOptimization']
    };
  }

  // UI / UX Design
  if (
    title.includes('ui/ux') ||
    title.includes('ux design') ||
    title.includes('user experience') ||
    title.includes('app design') ||
    title.includes('psychology behind great user')
  ) {
    return {
      id: 'ui-ux',
      name: 'UI / UX Design',
      badge: 'Design System & UX',
      type: 'Product Design',
      tags: ['#UIUXDesign', '#UserExperience', '#DesignSystem', '#ProductDesign']
    };
  }

  // Mobile App Development
  if (
    title.includes('mobile app') ||
    title.includes('android') ||
    title.includes('ios') ||
    title.includes('flutter') ||
    title.includes('react native') ||
    title.includes('progressive web app') ||
    title.includes('pwa')
  ) {
    return {
      id: 'mobile-app',
      name: 'Mobile Engineering',
      badge: 'Mobile App Architecture',
      type: 'Technical Architecture',
      tags: ['#MobileAppDevelopment', '#Android', '#iOS', '#AppArchitecture']
    };
  }

  // SEO & Digital Marketing
  if (
    title.includes('digital marketing') ||
    title.includes('seo and conversions') ||
    title.includes('seo') ||
    title.includes('marketing') ||
    title.includes('conversion')
  ) {
    return {
      id: 'marketing',
      name: 'SEO & Growth',
      badge: 'SEO & Performance Growth',
      type: 'Growth Blueprint',
      tags: ['#DigitalMarketing', '#SEOOptimization', '#BrandGrowth', '#ContentStrategy']
    };
  }

  // Enterprise & Cloud / Automation
  if (
    title.includes('erp') ||
    title.includes('crm') ||
    title.includes('odoo') ||
    title.includes('zoho') ||
    title.includes('qa & testing') ||
    title.includes('testing') ||
    title.includes('support & maintenance') ||
    title.includes('maintenance & support') ||
    title.includes('business process automation') ||
    title.includes('inventory management') ||
    title.includes('digital transformation') ||
    title.includes('it service models') ||
    title.includes('custom software development') ||
    title.includes('java api') ||
    title.includes('connection leak')
  ) {
    return {
      id: 'enterprise',
      name: 'Enterprise & Cloud',
      badge: 'Enterprise Architecture',
      type: 'Enterprise Solutions',
      tags: ['#EnterpriseSolutions', '#CloudArchitecture', '#DevOps', '#SoftwareEngineering']
    };
  }

  // Default: Web Development
  return {
    id: 'web-dev',
    name: 'Web Engineering',
    badge: 'Modern Web Architecture',
    type: 'Technical Guide',
    tags: ['#WebDevelopment', '#Frontend', '#FullStack', '#ModernWeb']
  };
};

const getCategoryIcon = (categoryId: string) => {
  switch (categoryId) {
    case 'ecommerce':
      return ShoppingCart;
    case 'ai-security':
      return ShieldCheck;
    case 'ai-ml':
      return Brain;
    case 'ui-ux':
      return Sparkles;
    case 'mobile-app':
      return CreditCard;
    case 'marketing':
      return BarChart3;
    default:
      return ShieldCheck;
  }
};

const getExecutiveSummaryTakeaways = (blog: BlogPost, tocItems: TocItem[]) => {
  // 1. AI Fraud Article (exact reference preservation)
  if (blog.slug === AI_FRAUD_SLUG) {
    return [
      {
        prefix: '99.5%+ Speed Advantage:',
        text: 'Machine learning models evaluate 100+ behavioral signals in under 50 milliseconds during checkout.'
      },
      {
        prefix: 'Slashing False Declines:',
        text: 'Replacing static rule thresholds preserves valid customer transactions while preventing card-not-present fraud.'
      },
      {
        prefix: 'Adaptive Step-Up Verification:',
        text: 'Moderate-risk flags trigger dynamic 2FA friction, stopping automated bots without hurting store conversions.'
      }
    ];
  }

  // 2. E-Commerce Website In 2026
  if (blog.slug.includes('ecommerce') || blog.slug.includes('e-commerce')) {
    return [
      {
        prefix: 'Modern E-Commerce Architecture in 2026:',
        text: 'Lightning-fast cloud hosting, mobile-first design, and secure headless architecture engineered for peak sales volume.'
      },
      {
        prefix: 'Frictionless Conversion UX:',
        text: 'Intuitive multi-step checkouts, transparent trust badges, and lightning-fast product filtering to eliminate cart abandonment.'
      },
      {
        prefix: 'End-to-End Payment Security:',
        text: 'Rigorous PCI compliance, tokenized gateways, and automated transaction monitoring to protect buyers and store owners.'
      }
    ];
  }

  // 3. Dynamic Takeaways for any other article based on excerpt & structure
  const excerptSentences = (blog.excerpt || '')
    .split(/(?<=[.!?])\s+/)
    .filter(s => s.trim().length > 15);

  const topicSummary = tocItems.length >= 2
    ? `Structured framework spanning ${tocItems.slice(0, 3).map(t => t.title).join(', ')}.`
    : 'Comprehensive engineering best practices and strategic architectural recommendations.';

  return [
    {
      prefix: 'Strategic Priority:',
      text: excerptSentences[0] || blog.excerpt || 'Modern digital architecture engineered for scalability, speed, and long-term business agility.'
    },
    {
      prefix: 'Key Technical Implementation:',
      text: topicSummary
    },
    {
      prefix: 'Measurable Business Impact:',
      text: excerptSentences[1] || 'Ensuring superior user experience, reduced technical debt, and maximum return on technological investment.'
    }
  ];
};

const renderArticleTitle = (titleText: string | undefined, isAiFraud: boolean) => {
  const text = titleText || 'Blog Article';

  // 1. Reference AI Fraud Page: Exact phrase
  const aiFraudTarget = 'AI to Detect Fraudulent';
  if (isAiFraud && text.includes(aiFraudTarget)) {
    const [before, ...rest] = text.split(aiFraudTarget);
    const after = rest.join(aiFraudTarget);
    return (
      <>
        <span className="block">{before.trim()}</span>
        <span className="block bg-gradient-to-r from-brand-600 via-brand-500 to-[#10B981] bg-clip-text text-transparent font-black my-0.5 sm:my-1">
          {aiFraudTarget}
        </span>
        <span className="block">{after.trim()}</span>
      </>
    );
  }

  // 2. Colon Titles e.g. "Digital Marketing: From Static to Dynamic"
  if (text.includes(':')) {
    const [before, ...rest] = text.split(':');
    const after = rest.join(':').trim();
    return (
      <>
        <span className="block">{before.trim()}:</span>
        <span className="block bg-gradient-to-r from-brand-600 via-brand-500 to-[#10B981] bg-clip-text text-transparent font-black my-0.5 sm:my-1">
          {after}
        </span>
      </>
    );
  }

  // 3. E-commerce in 2026 e.g. "How to Build a Successful E-Commerce Website in 2026"
  const in2026Match = text.match(/^(how to build a|how to develop a|building a)\s+(successful [a-z0-9\- ]+? website|[a-z0-9\- ]+? website)\s+(in 2026.*)$/i);
  if (in2026Match) {
    const [, before, highlight, after] = in2026Match;
    return (
      <>
        <span className="block">{before}</span>
        <span className="block bg-gradient-to-r from-brand-600 via-brand-500 to-[#10B981] bg-clip-text text-transparent font-black my-0.5 sm:my-1">
          {highlight}
        </span>
        <span className="block">{after}</span>
      </>
    );
  }

  // 4. General title split for rich aesthetic
  const words = text.split(/\s+/);
  if (words.length >= 6) {
    const pCount = Math.floor(words.length * 0.3);
    const hCount = Math.ceil(words.length * 0.4);
    const before = words.slice(0, pCount).join(' ');
    const highlight = words.slice(pCount, pCount + hCount).join(' ');
    const after = words.slice(pCount + hCount).join(' ');
    return (
      <>
        {before && <span className="block">{before}</span>}
        <span className="block bg-gradient-to-r from-brand-600 via-brand-500 to-[#10B981] bg-clip-text text-transparent font-black my-0.5 sm:my-1">
          {highlight}
        </span>
        {after && <span className="block">{after}</span>}
      </>
    );
  }

  if (words.length >= 3) {
    const before = words[0];
    const highlight = words.slice(1, words.length - 1).join(' ') || words[1];
    const after = words.length > 2 ? words.slice(words.length - 1).join(' ') : '';
    return (
      <>
        <span className="block">{before}</span>
        <span className="block bg-gradient-to-r from-brand-600 via-brand-500 to-[#10B981] bg-clip-text text-transparent font-black my-0.5 sm:my-1">
          {highlight}
        </span>
        {after && <span className="block">{after}</span>}
      </>
    );
  }

  return text;
};

const getAuthorInitials = (authorName: string | undefined | null) => {
  if (!authorName) return 'PS';
  const parts = authorName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return authorName.slice(0, 2).toUpperCase();
};

export default function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const normalizeSlug = (s?: string) => (s ? decodeURIComponent(s).toLowerCase().replace(/e-commerce/g, 'ecommerce').trim() : '');
  const isAiFraudArticle =
    slug === AI_FRAUD_SLUG || normalizeSlug(slug) === 'how-to-use-ai-to-detect-fraudulent-transactions-in-ecommerce';

  const serverBlogRaw = typeof window !== 'undefined' ? (window as any).__INITIAL_BLOG__ : undefined;
  const serverBlog =
    serverBlogRaw && (serverBlogRaw.slug === slug || normalizeSlug(serverBlogRaw.slug) === normalizeSlug(slug))
      ? ((window as any).__INITIAL_BLOG__ as BlogPost)
      : undefined;

  const passedBlog = (location.state as any)?.blog as BlogPost | undefined;
  const cachedDetail = slug ? blogService.getCachedBlogDetail(slug) : undefined;
  const cachedSummary = slug ? blogService.getCachedBlogSummary(slug) : undefined;

  const initialBlog =
    serverBlog ||
    cachedDetail?.data?.blog ||
    (passedBlog && (!slug || passedBlog.slug === slug) ? passedBlog : undefined) ||
    (cachedSummary && (!slug || cachedSummary.slug === slug) ? cachedSummary : undefined) ||
    (isAiFraudArticle ? AI_FRAUD_INITIAL_POST : null);

  const initialRelated = cachedDetail?.data?.relatedBlogs || [];

  const [blog, setBlog] = useState<BlogPost | null>(initialBlog);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>(initialRelated);
  const [loading, setLoading] = useState(!initialBlog);
  const [error, setError] = useState<string | null>(null);

  // Scroll spy, reading progress, and interaction states
  const [readingProgress, setReadingProgress] = useState(0);
  const [activeHeadingId, setActiveHeadingId] = useState<string>('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  useSeo({
    title: blog ? `${blog.title} | Prince IT Solution` : 'Blog Article | Prince IT Solution',
    description: blog?.excerpt || 'Read technology insights, guides, and software engineering articles on Prince IT Solution.',
    keywords: blog ? `${blog.title.toLowerCase()}, tech blog, Prince IT Solution` : 'tech blog',
    canonicalUrl: slug ? `https://www.princeitsolution.com/blog/${slug}` : 'https://www.princeitsolution.com/blog'
  });

  const fetchIdRef = useRef(0);

  // Newsletter state
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [newsletterError, setNewsletterError] = useState<string | null>(null);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    try {
      setSubmitting(true);
      setNewsletterError(null);
      const response = await newsletterService.subscribe(email, 'blog_detail');
      if (response.success) {
        setSubscribed(true);
        setEmail('');
      } else {
        setNewsletterError(response.message || 'Subscription failed');
      }
    } catch (err) {
      setNewsletterError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2200);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveHeadingId(id);
      setMobileTocOpen(false);
    }
  };

  // Scroll spy & reading progress calculation (applies to all articles)
  useEffect(() => {
    const handleScroll = () => {
      // 1. Reading progress calculation
      const articleEl = document.getElementById('blog-article-body') || document.getElementById('ai-fraud-article-body');
      if (articleEl) {
        const rect = articleEl.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const totalHeight = articleEl.offsetHeight;
        const scrolled = -rect.top + 180;
        const maxScroll = Math.max(1, totalHeight - (windowHeight / 2));
        if (scrolled <= 0) {
          setReadingProgress(0);
        } else if (scrolled >= maxScroll) {
          setReadingProgress(100);
        } else {
          setReadingProgress(Math.min(100, Math.max(0, Math.round((scrolled / maxScroll) * 100))));
        }
      }

      // 2. Heading spy
      const headingEls = document.querySelectorAll('#blog-article-body h2[id], #ai-fraud-article-body h2[id]');
      let current = '';
      headingEls.forEach((el) => {
        const top = el.getBoundingClientRect().top;
        if (top <= 220) {
          current = el.id;
        }
      });
      if (current) {
        setActiveHeadingId(current);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [blog?.content, slug]);

  useEffect(() => {
    if (!slug) return;

    // Check if we already have full content and related posts
    const currentCached = blogService.getCachedBlogDetail(slug);
    if (currentCached?.data?.blog?.content && (!blog || !blog.content)) {
      setBlog(currentCached.data.blog);
      if (currentCached.data.relatedBlogs) {
        setRelatedPosts(currentCached.data.relatedBlogs);
      }
      setLoading(false);
    }

    const fetchId = ++fetchIdRef.current;

    const fetchBlog = async () => {
      try {
        if (!blog) {
          setLoading(true);
        }
        setError(null);
        const response = await blogService.getBlogBySlug(slug);

        if (fetchId !== fetchIdRef.current) return;

        const blogData = (response as any).data?.blog || (response as any).data;
        const related = (response as any).data?.relatedBlogs || [];

        if (response.status && blogData && (blogData.title || blogData.id)) {
          setBlog(blogData);
          setRelatedPosts(related);
        } else {
          if (!blog) {
            setError('Blog post not found or invalid response');
          }
        }
      } catch (err) {
        if (fetchId !== fetchIdRef.current) return;
        if (!blog) {
          setError(err instanceof Error ? err.message : 'An error occurred while fetching the article');
        }
      } finally {
        if (fetchId === fetchIdRef.current) {
          setLoading(false);
        }
      }
    };

    fetchBlog();
  }, [slug]);

  if (loading && !blog) {
    return (
      <div className="min-h-screen bg-[#030712] relative overflow-hidden flex items-center justify-center">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: 'radial-gradient(circle at center, rgba(var(--app-primary-rgb), 0.22) 1.5px, transparent 1.5px)',
            backgroundSize: '32px 32px'
          }}
        />
        <Loader2 className="w-8 h-8 text-brand-400 animate-spin relative z-10" />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#030712] px-4 text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mb-6" />
        <h2 className="text-2xl font-display font-bold text-white mb-4">Oops! Something went wrong</h2>
        <p className="text-slate-400 mb-8 max-w-md">{error || "We couldn't find the article you're looking for."}</p>
        <Link to="/blog" className="flex items-center gap-2 text-brand-400 hover:text-brand-300 transition-colors font-medium">
          <ArrowLeft className="w-5 h-5" />
          Back to Blog Insights
        </Link>
      </div>
    );
  }

  const rawHtml = blog.content || '';

  // Auto-wrap plain tables in responsive scroll wrapper and strip redundant figure.table wrappers
  let cleanHtml = rawHtml
    .replace(/<p>(?:\s|&nbsp;|<br\/?\>)*<\/p>/gi, '') // Remove paragraphs that only contain spaces, &nbsp;, or a <br>
    .replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br/>') // Replace multiple <br> tags with a single <br>
    .replace(/<figure[^>]*class="[^"]*table[^"]*"[^>]*>/gi, '') // Strip outer figure.table
    .replace(/<\/figure>/gi, '');

  if (cleanHtml.includes('<table') && !cleanHtml.includes('table-responsive')) {
    cleanHtml = cleanHtml
      .replace(/<table/gi, '<div class="table-responsive"><table')
      .replace(/<\/table>/gi, '</table></div>');
  }

  // Strip empty paragraphs wrapping table-responsive
  cleanHtml = cleanHtml
    .replace(/<p>\s*(<div class="table-responsive">)/gi, '$1')
    .replace(/(<\/div>)\s*<\/p>/gi, '$1');

  // If there are no <h2> tags in the content, elevate standalone short <p><strong>...</strong></p> into <h2>
  if (!/<h2/i.test(cleanHtml)) {
    cleanHtml = cleanHtml.replace(
      /<p><strong>([A-Za-z0-9][^<]{3,90}[A-Za-z0-9\?!:])<\/strong><\/p>/gi,
      '<h2>$1</h2>'
    );
  }

  // Generate anchor IDs on headings with matching section numbers (01-XX) and build dynamic TOC
  const dynamicToc: TocItem[] = [];
  let sectionCounter = 0;

  const processedHtml = cleanHtml
    .replace(/<h2[^>]*>(?:\s|&nbsp;|<br\/?\>)*<\/h2>/gi, '') // Strip empty <h2>&nbsp;</h2>
    .replace(/<h2([^>]*)>(.*?)<\/h2>/gi, (match, attrs, inner) => {
      const plainText = inner.replace(/<[^>]+>/g, '').replace(/&[a-z0-9#]+;/gi, ' ').trim();
      if (!plainText) return '';
      const headingId = plainText
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      sectionCounter++;
      let num = String(sectionCounter).padStart(2, '0');
      let title = plainText;

      if (isAiFraudArticle) {
        const tocMatch = AI_FRAUD_TOC.find(item => item.id === headingId);
        if (tocMatch) {
          num = tocMatch.num;
          title = tocMatch.title;
        }
      }

      dynamicToc.push({ id: headingId, num, title });

      return `<h2 id="${headingId}" class="scroll-mt-32 font-display ai-fraud-heading-numbered flex items-start gap-3 sm:gap-4"><span class="ai-fraud-section-badge flex-shrink-0 font-mono select-none">${num}</span><span class="flex-1 min-w-0">${inner}</span></h2>`;
    });

  const activeToc = isAiFraudArticle && dynamicToc.length === 0 ? AI_FRAUD_TOC : dynamicToc;

  const sanitizedContent = DOMPurify.sanitize(processedHtml, {
    ADD_TAGS: ['iframe', 'mark', 'figure', 'figcaption', 'sub', 'sup', 'input', 'hr', 'span'],
    ADD_ATTR: ['id', 'allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src', 'style', 'class', 'type', 'checked', 'disabled', 'target', 'rel']
  });

  const formatDate = (dateStr: string | undefined | null) => {
    if (!dateStr) return 'Date unavailable';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Invalid date';
      return format(date, 'MMMM dd, yyyy');
    } catch (e) {
      return 'Date unavailable';
    }
  };

  const readingTime = blog.content
    ? Math.ceil(blog.content.split(/\s+/).length / 200)
    : 0;

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=2072';
  };

  const isLight = theme === 'light';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : `https://www.princeitsolution.com/blog/${slug}`;

  const categoryInfo = getArticleCategoryInfo(blog);
  const CategoryIcon = getCategoryIcon(categoryInfo.id);
  const takeaways = getExecutiveSummaryTakeaways(blog, activeToc);

  const browserFrameTitle = isAiFraudArticle
    ? 'E-Commerce Fraud Prevention AI Architecture'
    : `${categoryInfo.badge.toUpperCase()} ARCHITECTURE`;

  return (
    <div className={`blog-article-page min-h-screen relative overflow-x-clip w-full ${isLight ? 'bg-[#f8fafc] text-slate-900' : 'bg-[#030712] text-slate-100'}`}>
      {/* Fixed Top Reading Progress Bar */}
      <div
        className="fixed top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-brand-600 via-brand-500 to-[#10B981] z-50 transition-all duration-150 shadow-[0_0_12px_rgba(16,185,129,0.5)]"
        style={{ width: `${readingProgress}%` }}
      />

      {/* Editorial Ambient Background Glow & Geometric Accents matching /blog */}
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Subtle Tech Dot Matrix */}
        <div
          className={`blog-dot-matrix absolute inset-0 ${
            isLight ? 'opacity-[0.06]' : 'opacity-[0.16]'
          }`}
          style={{
            backgroundImage: isLight
              ? 'radial-gradient(circle at center, var(--color-brand-600) 1px, transparent 1px)'
              : 'radial-gradient(circle at center, var(--color-brand-400) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}
        />

        {/* Soft Radial Ambient Aura Blurs matching /blog */}
        <div
          className={`absolute -top-24 right-0 w-[650px] h-[650px] rounded-full blur-[140px] ${
            isLight ? 'bg-sky-200/40' : 'bg-brand-500/10'
          }`}
        />
        <div
          className={`absolute top-1/4 -left-20 w-[550px] h-[550px] rounded-full blur-[160px] ${
            isLight ? 'bg-indigo-100/60' : 'bg-brand-600/10'
          }`}
        />
        <div
          className={`absolute top-2/3 right-10 w-[600px] h-[600px] rounded-full blur-[150px] ${
            isLight ? 'bg-brand-100/40' : 'bg-brand-600/8'
          }`}
        />
      </div>

      {/* Hero Section */}
      <header className="relative pt-32 sm:pt-36 md:pt-40 pb-12 md:pb-20 z-10 overflow-hidden">
        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Center Editorial Column */}
          <div
            className="flex flex-col items-center text-center max-w-xl lg:max-w-3xl 2xl:max-w-4xl mx-auto space-y-6 relative z-20"
          >
            {/* Category Badges Row */}
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <Link
                to="/blog"
                className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full transition-all duration-200 shadow-xs ${
                  isLight
                    ? 'bg-brand-100/60 text-brand-600 border border-brand-200 hover:bg-brand-100'
                    : 'bg-brand-600/15 text-brand-300 border border-brand-600/30 hover:bg-brand-600/25'
                }`}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                ALL ARTICLES
              </Link>

              <span
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-1.5 rounded-full shadow-xs ${
                  isLight
                    ? 'bg-[#D1FAE5]/70 text-[#065F46] border border-[#A7F3D0]'
                    : 'bg-[#10B981]/15 text-emerald-300 border border-[#10B981]/30'
                }`}
              >
                <CategoryIcon className="w-4 h-4 text-[#10B981]" />
                {categoryInfo.badge}
              </span>

              <span
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3.5 py-1.5 rounded-full shadow-xs ${
                  isLight
                    ? 'bg-slate-100/90 text-slate-600 border border-slate-200/80'
                    : 'bg-[#15233E]/80 text-slate-300 border border-white/10'
                }`}
              >
                {categoryInfo.type}
              </span>
            </div>

            {/* Dominant Headline */}
            <h1
              className={`text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] xl:text-[3.5rem] font-display font-extrabold sm:font-black tracking-tight leading-[1.14] ${
                isLight ? 'text-[#0F172A]' : 'text-white'
              }`}
            >
              {renderArticleTitle(blog.title, isAiFraudArticle)}
            </h1>

            {/* Excerpt / Summary */}
            {blog.excerpt && (
              <p
                className={`text-sm sm:text-base md:text-lg font-normal leading-relaxed max-w-3xl ${
                  isLight ? 'text-slate-600' : 'text-slate-300'
                }`}
              >
                {blog.excerpt}
              </p>
            )}

            {/* Author, Date, Reading Time Metadata Row */}
            <div
              className={`flex flex-wrap items-center justify-center gap-3 sm:gap-5 pt-2 text-xs sm:text-sm font-medium ${
                isLight ? 'text-slate-600' : 'text-slate-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-600 to-[#10B981] text-white flex items-center justify-center font-bold text-xs shadow-xs">
                  {getAuthorInitials(blog.author)}
                </div>
                <span className={`font-semibold ${isLight ? 'text-slate-800' : 'text-slate-200'}`}>
                  {blog.author || 'Tiya Chavda'}
                </span>
              </div>

              <span className={`${isLight ? 'text-slate-300' : 'text-slate-600'} select-none`}>•</span>

              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-[#10B981]" />
                <span>{formatDate(blog.published_at)}</span>
              </div>

              <span className={`${isLight ? 'text-slate-300' : 'text-slate-600'} select-none`}>•</span>

              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-brand-500" />
                <span>{readingTime || 6} min read</span>
              </div>
            </div>
          </div>

          {/* Featured Image Display (Centered with Browser Frame) */}
          {blog.featured_image && (
            <div
              className="mt-12 sm:mt-14 max-w-5xl mx-auto relative z-20"
            >
              <div
                className={`rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-300 ${
                  isLight
                    ? 'border border-slate-200/90 shadow-[0_20px_50px_rgba(15,23,42,0.08),0_4px_16px_rgba(0,0,0,0.03)] bg-white'
                    : 'border border-brand-600/25 shadow-[0_20px_50px_rgba(0,0,0,0.5),0_0_30px_rgba(var(--app-primary-rgb),0.10)] bg-[#101B2D]'
                }`}
              >
                {/* Browser Window Top Bar */}
                <div
                  className={`px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between border-b text-xs ${
                    isLight
                      ? 'bg-slate-50/90 border-slate-200/80 text-slate-500'
                      : 'bg-[#131F37]/90 border-white/10 text-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-wider text-[#10B981] font-bold truncate max-w-md">
                    {browserFrameTitle}
                  </span>
                  <span className="text-[11px] font-sans font-medium opacity-70">
                    Prince IT Solution
                  </span>
                </div>

                <div className="relative overflow-hidden bg-slate-950/10 dark:bg-slate-950/30">
                  <img
                    src={blogService.getFeaturedImageUrl(blog.featured_image, blog.image_url)}
                    alt={blog.title || 'Blog post featured illustration'}
                    className="w-full h-auto block object-cover max-h-[620px]"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    onError={handleImageError}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mobile On-This-Page Floating Drawer/Toggle */}
      {activeToc.length > 0 && (
        <div className="lg:hidden sticky top-20 z-30 px-4 py-2">
          <div
            className={`max-w-8xl mx-auto rounded-xl p-3 border shadow-md backdrop-blur-md transition-all ${
              isLight
                ? 'bg-white/95 border-slate-200 text-slate-800'
                : 'bg-[#090e17]/95 border-brand-500/20 text-white'
            }`}
          >
            <button
              onClick={() => setMobileTocOpen(!mobileTocOpen)}
              className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-wider cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <List className="w-4 h-4 text-brand-400" />
                <span>On This Page ({activeToc.length} Sections)</span>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileTocOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobileTocOpen && (
              <div className="mt-3 pt-3 border-t border-white/5 space-y-1">
                {activeToc.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full text-left py-1.5 px-2 rounded-lg text-xs transition-colors flex items-center justify-between cursor-pointer ${
                      activeHeadingId === item.id
                        ? isLight
                          ? 'bg-brand-50 text-brand-700 font-semibold'
                          : 'bg-brand-500/15 text-brand-300 font-semibold'
                        : isLight
                        ? 'text-slate-600 hover:bg-slate-50'
                        : 'text-slate-400 hover:bg-white/5'
                    }`}
                  >
                    <span className="line-clamp-1">{item.title}</span>
                    <span className="text-[10px] font-mono opacity-60 ml-2">{item.num}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Editorial Content & Supporting Sidebar */}
      <main className="py-6 sm:py-8 md:py-12 relative pointer-events-auto">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_340px] gap-8 xl:gap-12 items-start">
            
            {/* Article Column */}
            <div className="min-w-0 w-full space-y-8">
              {/* Executive Summary Callout */}
              <div
                className={`p-6 sm:p-7 rounded-2xl border transition-all relative z-10 ${
                  isLight
                    ? 'bg-brand-50 border-brand-200/90 text-slate-800 shadow-sm'
                    : 'bg-brand-500/[0.06] border-brand-500/25 text-slate-200 shadow-[0_10px_30px_rgba(var(--app-primary-rgb),0.08)]'
                }`}
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <Sparkles className="w-5 h-5 text-brand-400" />
                  <h3 className={`font-display font-bold text-base sm:text-lg ${isLight ? 'text-brand-950' : 'text-brand-300'}`}>
                    Executive Summary & Key Takeaways
                  </h3>
                </div>
                <ul className="space-y-2 text-xs sm:text-sm leading-relaxed opacity-90">
                  {takeaways.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-brand-400 font-bold mt-0.5">•</span>
                      <span><strong>{item.prefix}</strong> {item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Article Body Card */}
              <article
                className={`p-6 sm:p-10 md:p-12 xl:p-14 rounded-2xl sm:rounded-3xl border shadow-xl transition-all duration-300 w-full min-w-0 relative z-10 ${
                  isLight
                    ? 'bg-white border-slate-200/80 shadow-[0_15px_45px_rgba(15,23,42,0.06)]'
                    : 'bg-[#090e17]/95 border-white/8 shadow-[0_20px_50px_rgba(0,0,0,0.5)]'
                }`}
              >
                <div
                  id="blog-article-body"
                  className={`blog-content blog-article-content ${isLight ? 'text-slate-800' : 'text-slate-200'}`}
                >
                  {blog.content ? (
                    parse(sanitizedContent)
                  ) : (
                    <div className="space-y-4 py-4 animate-pulse">
                      <div className={`h-7 rounded-lg w-3/4 ${isLight ? 'bg-slate-200' : 'bg-white/10'}`} />
                      <div className={`h-4 rounded w-full ${isLight ? 'bg-slate-200' : 'bg-white/10'}`} />
                      <div className={`h-4 rounded w-5/6 ${isLight ? 'bg-slate-200' : 'bg-white/10'}`} />
                      <div className={`h-4 rounded w-2/3 ${isLight ? 'bg-slate-200' : 'bg-white/10'}`} />
                      <div className={`h-40 rounded-xl w-full mt-6 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} />
                    </div>
                  )}
                </div>

                {/* Article Tags & Bottom Share Strip */}
                <div className={`mt-12 pt-8 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-400 mr-1">Tags:</span>
                    {categoryInfo.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`text-xs px-2.5 py-1 rounded-full ${
                          isLight
                            ? 'bg-slate-100 text-slate-700'
                            : 'bg-white/5 text-slate-300 border border-white/5'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-slate-400">Share:</span>
                    <button
                      onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(blog.title)}`, '_blank')}
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                        isLight
                          ? 'bg-slate-100 text-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white'
                          : 'bg-white/5 text-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white border border-white/10'
                      }`}
                      aria-label="Share on X / Twitter"
                    >
                      <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.84 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" /></svg>
                    </button>

                    <button
                      onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank')}
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                        isLight
                          ? 'bg-slate-100 text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white'
                          : 'bg-white/5 text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white border border-white/10'
                      }`}
                      aria-label="Share on LinkedIn"
                    >
                      <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                    </button>

                    <button
                      onClick={handleCopyLink}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                        copiedLink
                          ? 'bg-emerald-500 text-slate-950 font-bold'
                          : isLight
                          ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          : 'bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10'
                      }`}
                    >
                      {copiedLink ? (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5 text-brand-400" />
                          Copy Link
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </article>
            </div>

            {/* Supporting Sticky Sidebar (Desktop >= lg) */}
            <aside className="hidden lg:block w-full sticky top-28 self-start space-y-6 relative z-10">
              
              {/* 1. Reading Progress Widget */}
              <div
                className={`p-5 rounded-2xl border transition-all ${
                  isLight
                    ? 'bg-white border-slate-200/90 shadow-sm'
                    : 'bg-[#090d16]/90 border-white/8 shadow-md'
                }`}
              >
                <div className="flex items-center justify-between text-xs mb-2.5">
                  <span className="font-semibold text-slate-400 flex items-center gap-1.5">
                    <BookOpen className="w-3.5 h-3.5 text-brand-400" />
                    Reading Progress
                  </span>
                  <span className="font-mono font-bold text-brand-400">{readingProgress}%</span>
                </div>
                <div className={`w-full h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-slate-100' : 'bg-white/10'}`}>
                  <div
                    className="h-full bg-gradient-to-r from-brand-400 to-emerald-400 transition-all duration-150"
                    style={{ width: `${readingProgress}%` }}
                  />
                </div>
              </div>

              {/* 2. Compact "On This Page" Table of Contents */}
              {activeToc.length > 0 && (
                <div
                  className={`p-5 rounded-2xl border transition-all ${
                    isLight
                      ? 'bg-white border-slate-200/90 shadow-sm'
                      : 'bg-[#090d16]/90 border-white/8 shadow-md'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/5">
                    <h4 className={`text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
                      <List className="w-4 h-4 text-brand-400" />
                      On This Page
                    </h4>
                    <span className="text-[11px] font-mono text-brand-400/80 font-medium">{activeToc.length} Sections</span>
                  </div>

                  <nav className="space-y-1">
                    {activeToc.map((item) => {
                      const isActive = activeHeadingId === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className={`w-full text-left py-2 px-2.5 rounded-xl text-xs transition-all flex items-start justify-between group cursor-pointer ${
                            isActive
                              ? isLight
                                ? 'bg-brand-50 text-brand-800 font-bold border-l-2 border-brand-500 shadow-xs'
                                : 'bg-brand-500/15 text-brand-300 font-bold border-l-2 border-brand-400'
                              : isLight
                              ? 'text-slate-600 hover:text-slate-950 hover:bg-slate-50'
                              : 'text-slate-400 hover:text-white hover:bg-white/5'
                          }`}
                        >
                          <span className="leading-snug pr-2 line-clamp-2">{item.title}</span>
                          <span
                            className={`text-[10px] font-mono transition-opacity flex-shrink-0 ml-1 ${
                              isActive ? 'opacity-100 text-brand-400' : 'opacity-40 group-hover:opacity-80'
                            }`}
                          >
                            {item.num}
                          </span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              )}

              {/* 3. Compact Article Information Card */}
              <div
                className={`p-5 rounded-2xl border transition-all ${
                  isLight
                    ? 'bg-white border-slate-200/90 shadow-sm'
                    : 'bg-[#090d16]/90 border-white/8 shadow-md'
                }`}
              >
                <h4 className={`text-xs font-bold uppercase tracking-wider mb-4 pb-2 border-b ${isLight ? 'border-slate-100 text-slate-900' : 'border-white/5 text-white'}`}>
                  Article Metadata
                </h4>
                <div className="space-y-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Author:</span>
                    <span className="font-semibold text-brand-400">{blog.author || 'Tiya Chavda'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Published:</span>
                    <span className={isLight ? 'text-slate-700' : 'text-slate-200'}>{formatDate(blog.published_at)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Read Time:</span>
                    <span className={isLight ? 'text-slate-700' : 'text-slate-200'}>{readingTime || 6} Minutes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Category:</span>
                    <span className="font-medium text-emerald-400">{categoryInfo.name}</span>
                  </div>
                </div>
              </div>

              {/* 4. Clean Share Actions Panel */}
              <div
                className={`p-5 rounded-2xl border transition-all ${
                  isLight
                    ? 'bg-white border-slate-200/90 shadow-sm'
                    : 'bg-[#090d16]/90 border-white/8 shadow-md'
                }`}
              >
                <h4 className={`text-xs font-bold uppercase tracking-wider mb-3.5 flex items-center gap-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
                  <Share2 className="w-3.5 h-3.5 text-brand-400" />
                  Share This Guide
                </h4>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <button
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(blog.title)}`, '_blank')}
                    className={`h-9 rounded-xl flex items-center justify-center transition-all cursor-pointer ${
                      isLight
                        ? 'bg-slate-100 text-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white'
                        : 'bg-white/5 text-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white border border-white/5'
                    }`}
                    aria-label="Share on Twitter"
                    title="Share on Twitter / X"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.84 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" /></svg>
                  </button>

                  <button
                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank')}
                    className={`h-9 rounded-xl flex items-center justify-center transition-all cursor-pointer ${
                      isLight
                        ? 'bg-slate-100 text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white'
                        : 'bg-white/5 text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white border border-white/5'
                    }`}
                    aria-label="Share on LinkedIn"
                    title="Share on LinkedIn"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                  </button>

                  <button
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
                    className={`h-9 rounded-xl flex items-center justify-center transition-all cursor-pointer ${
                      isLight
                        ? 'bg-slate-100 text-[#1877F2] hover:bg-[#1877F2] hover:text-white'
                        : 'bg-white/5 text-[#1877F2] hover:bg-[#1877F2] hover:text-white border border-white/5'
                    }`}
                    aria-label="Share on Facebook"
                    title="Share on Facebook"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                  </button>

                  <button
                    onClick={handleCopyLink}
                    className={`h-9 rounded-xl flex items-center justify-center transition-all cursor-pointer ${
                      copiedLink
                        ? 'bg-emerald-500 text-slate-950 font-bold'
                        : isLight
                        ? 'bg-slate-100 text-brand-600 hover:bg-brand-500 hover:text-white'
                        : 'bg-white/5 text-brand-400 hover:bg-brand-500/20 hover:text-brand-300 border border-white/5'
                    }`}
                    aria-label="Copy Link"
                    title="Copy Article Link"
                  >
                    {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* 5. Stay Updated / Newsletter Subscription */}
              <div
                className={`p-5 rounded-2xl border transition-all ${
                  isLight
                    ? 'bg-white border-slate-200/90 shadow-sm'
                    : 'bg-[#090d16]/90 border-white/8 shadow-md'
                }`}
              >
                <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
                  Tech Insights Weekly
                </h4>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                  Get hand-curated engineering articles and technology trends in your inbox.
                </p>

                {subscribed ? (
                  <div className="flex flex-col items-center text-center py-3">
                    <CheckCircle2 className="w-8 h-8 text-brand-400 mb-2" />
                    <p className="text-xs font-bold text-white">Subscribed!</p>
                    <p className="text-[11px] text-slate-400 mt-1">Thank you for joining our community.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="space-y-3">
                    <div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@company.com"
                        required
                        disabled={submitting}
                        className={`w-full rounded-xl py-2 px-3 text-xs focus:outline-none transition-all ${
                          isLight
                            ? 'bg-slate-50 text-slate-900 placeholder-slate-400 border border-slate-200 focus:border-brand-500'
                            : 'bg-black/40 text-white placeholder-slate-500 border border-white/10 focus:border-brand-400'
                        }`}
                      />
                      {newsletterError && (
                        <p className="text-red-400 text-[10px] mt-1.5 px-1">{newsletterError}</p>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`w-full py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                        isLight
                          ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm'
                          : 'bg-gradient-to-r from-brand-400 to-emerald-400 text-slate-950 hover:brightness-110 shadow-[0_0_15px_rgba(var(--app-primary-rgb),0.25)]'
                      }`}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Subscribing...
                        </>
                      ) : (
                        'Subscribe to Updates'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </aside>

          </div>
        </div>
      </main>

      {/* Preserved Related Posts Section */}
      {relatedPosts.length > 0 && (
        <section className={`py-12 md:py-20 border-t ${isLight ? 'border-slate-200' : 'border-white/5'}`}>
          <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-end gap-4 mb-8 md:mb-12">
              <div>
                <h2 className={`text-2xl sm:text-3xl font-display font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
                  Continue Reading
                </h2>
                <p className={`text-sm ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
                  Explore more technology articles and architectural guides.
                </p>
              </div>
              <Link to="/blog" className="text-brand-400 hover:text-brand-300 font-medium text-sm transition-colors flex items-center gap-1.5">
                View all posts
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
              {relatedPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group block"
                >
                  <div className={`aspect-video rounded-xl overflow-hidden mb-3.5 transition-all duration-300 ${
                    isLight
                      ? 'border border-slate-200 shadow-sm group-hover:shadow-md'
                      : 'border border-white/10 group-hover:border-brand-500/40 shadow-sm'
                  }`}>
                    <img
                      src={blogService.getFeaturedImageUrl(post.featured_image, post.image_url)}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      onError={handleImageError}
                    />
                  </div>
                  <h3 className={`font-bold text-sm sm:text-base group-hover:text-brand-400 transition-colors line-clamp-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
                    {post.title}
                  </h3>
                  <p className="text-slate-400 text-xs mt-1.5">
                    {formatDate(post.published_at)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
